/**
 * The seven-layer house.
 *
 * Which layer of the Sponsorship Operating System a module belongs to, so a
 * card can say what it is part of without the reader having to know the
 * architecture. Source: `PNET_DataBrokerageSystem_AgentHandover_v1.2` §2
 * (decision D1, Sep 2026 — seven layers, superseding any "six layers"
 * phrasing).
 *
 * Order is roof to foundation, as the house is drawn. Do not reorder to suit a
 * screen: the sequence is the architecture.
 *
 * Icons are the app registry's nearest match to the brand deck's glyph set
 * (`partnershipnet-brand/assets/icons`), so a layer reads the same in a deck
 * and on a card: scales for pricing, compass for finding, shield for
 * evidencing, gauge for running, database for the foundation.
 */

export const HOUSE_LAYER_IDS = [
  'kaas',
  'marketplace',
  'valuation',
  'scouting',
  'data-brokerage',
  'platform',
  'data-management',
] as const;

export type HouseLayer = (typeof HOUSE_LAYER_IDS)[number];

export interface HouseLayerMeta {
  readonly id: HouseLayer;
  /** Full name, used in the tooltip. */
  readonly label: string;
  /** Short name, used on the chip where space is tight. */
  readonly short: string;
  /** The layer's verb in the house table — what a decision gets here. */
  readonly verb: string;
  readonly icon: string;
}

export const HOUSE_LAYERS: readonly HouseLayerMeta[] = [
  {
    id: 'kaas',
    label: 'Knowledge as a Service',
    short: 'Knowledge',
    verb: 'Advised',
    icon: 'book',
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    short: 'Marketplace',
    verb: 'Traded',
    icon: 'handshake',
  },
  { id: 'valuation', label: 'Valuation', short: 'Valuation', verb: 'Priced', icon: 'scale' },
  { id: 'scouting', label: 'Scouting', short: 'Scouting', verb: 'Sourced', icon: 'compass' },
  {
    id: 'data-brokerage',
    // "Data Brokerage System" on first mention, "Data Brokerage" as the short
    // label. DBS is internal-only and never appears in the interface.
    label: 'Data Brokerage System',
    short: 'Data Brokerage',
    verb: 'Evidenced',
    icon: 'shield',
  },
  { id: 'platform', label: 'Platform', short: 'Platform', verb: 'Run', icon: 'gauge' },
  {
    id: 'data-management',
    label: 'Data Management',
    short: 'Data Management',
    verb: 'Trusted',
    icon: 'database',
  },
];

const BY_ID = new Map(HOUSE_LAYERS.map((l) => [l.id, l]));

export const houseLayer = (id: HouseLayer): HouseLayerMeta | undefined => BY_ID.get(id);
